import { useMemo } from 'react';

import { UnitSystem } from '@ravago/shared/page-data/models/config';
import { BasicFacetTermV2, BasicFacetWithTermsV2, ProductRequestType } from '@ravago/shared/page-data/models/elastic';

import { useQueryStates } from 'nuqs';

import { facetsObjectToList } from '../utils/facet-nuqs-utils';
import { createProductQueryParsers } from '../utils/product-search-nuqs-params';

interface Props {
   defaultCountry?: string;
   defaultUnitSystem: UnitSystem;
   defaultFacetFilters: BasicFacetWithTermsV2[];
}

export const useProductPortfolioSearchParams = ({ defaultCountry, defaultUnitSystem, defaultFacetFilters }: Props) => {
   const parsers = useMemo(() => createProductQueryParsers(defaultCountry, defaultUnitSystem), [defaultCountry, defaultUnitSystem]);
   const [queryStates, setQueryStates] = useQueryStates(parsers);
   const { page, search, tab, view, unitSystem, ...facetFilters } = queryStates;

   const parsedFacets = useMemo(() => facetsObjectToList(facetFilters), [facetFilters]);

   const facetsListToObject = (value: Array<BasicFacetWithTermsV2>) => {
      const queryValue: Record<string, BasicFacetTermV2[] | null> = Object.fromEntries(Object.keys(facetFilters).map((key) => [key, []]));
      value.forEach((facet) => {
         queryValue[facet.facetName] = facet.terms;
      });
      return queryValue;
   };

   const getUnitSystemForView = (view: 'grid' | 'list', preferredUnitSystem?: UnitSystem) => {
      if (view === 'grid') {
         return null;
      }
      return preferredUnitSystem ?? defaultUnitSystem;
   };

   const setTab = (newTab: ProductRequestType, preferredView?: 'grid' | 'list', preferredUnitSystem?: UnitSystem) => {
      const newView = newTab === 'product' && preferredView ? preferredView : parsers.view.defaultValue;
      return setQueryStates({
         tab: newTab,
         page: 1,
         view: newView,
         unitSystem: newTab === 'product' ? getUnitSystemForView(newView, preferredUnitSystem) : null
      });
   };

   const setSearch = (query: string) =>
      setQueryStates({
         search: query,
         page: 1
      });

   const setPage = (newPage: number) =>
      setQueryStates({
         page: newPage
      });

   const setView = (newView: 'grid' | 'list', preferredUnitSystem?: UnitSystem) =>
      setQueryStates({
         view: newView,
         page: 1,
         unitSystem: getUnitSystemForView(newView, preferredUnitSystem)
      });

   const setUnitSystem = (newUnitSystem: UnitSystem) =>
      setQueryStates({
         page: 1,
         unitSystem: newUnitSystem,
         ...facetsListToObject(defaultFacetFilters)
      });

   const setFacets = (newFacets: BasicFacetWithTermsV2[]) =>
      setQueryStates({
         ...facetsListToObject(newFacets),
         page: 1
      });

   const resetFacets = () =>
      setQueryStates({
         ...facetsListToObject(defaultFacetFilters),
         page: 1
      });

   return {
      tab,
      search,
      page,
      view,
      unitSystem,
      facetFilters: parsedFacets,
      setTab,
      setSearch,
      setPage,
      setView,
      setUnitSystem,
      setFacets,
      resetFacets
   };
};
